import { useCallback, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { PickedPerson } from "./person-picker"
import type { IndexModel } from "@/api/models"

import { QuickStartStartError, TenderFileUploadError } from "@/api/tender-api"
import { AlertTriangle, Library, Loader2, Upload, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useConfigQuery } from "@/hooks/use-config-query"

import { useQuickStartTenderCase, useRetryTenderAutoPipelineStart } from "../hooks/use-tender-cases"
import { TENDER_ACCEPTED_EXTENSIONS_WITH_ZIP } from "../tender-upload-constants"
import { QuickStartDeadlineFields } from "./quick-start-deadline-fields"
import { FileList } from "./quick-start-file-list"
import { QuickStartPeopleFields } from "./quick-start-people-fields"
import { isDateOnly, isPlausibleDate } from "../utils/deadline"
import { detectCvLibrary } from "../utils/detect-cv-library"
import {
  captureDropEntries,
  formatFileSize,
  resolveEntriesToFiles,
  resolveFileList,
} from "../utils/resolve-drop-files"

// Batch caps. The backend SAS-issuing endpoint also enforces 500 files; we
// stop here at the same number so the user sees the friendly UI message
// instead of a 400 from the API. 5 GB total is generous for a typical
// procurement bundle and avoids accidentally pushing TBs through Azure.
const MAX_FILE_COUNT = 500
const MAX_TOTAL_SIZE = 5 * 1024 * 1024 * 1024 // 5 GB

type Props = {
  open: boolean
  onClose: () => void
  cvLibraryKeyword?: string
}

export function QuickStartModal({ open, onClose, cvLibraryKeyword = "cv" }: Props) {
  const { t } = useTranslation()
  const quickStart = useQuickStartTenderCase()
  const retryStart = useRetryTenderAutoPipelineStart()
  const { data: config } = useConfigQuery()
  const libraries: IndexModel[] = config?.indexes ?? []

  const [title, setTitle] = useState("")
  const [procuringAuthority, setProcuringAuthority] = useState("")
  const [sourceDocumentsUrl, setSourceDocumentsUrl] = useState("")
  const [submissionDeadlineDraft, setSubmissionDeadlineDraft] = useState("")
  const [questionsDeadlineDraft, setQuestionsDeadlineDraft] = useState("")
  const [responsible, setResponsible] = useState<PickedPerson[]>([])
  const [participants, setParticipants] = useState<PickedPerson[]>([])
  const [files, setFiles] = useState<File[]>([])
  const [overrideFileIndexes, setOverrideFileIndexes] = useState<number[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [processingCount, setProcessingCount] = useState(0)
  const isProcessingDrop = processingCount > 0
  const [dropWarnings, setDropWarnings] = useState<string[]>([])
  const [uploadPercent, setUploadPercent] = useState<number | null>(null)
  // Set when the case + documents were created but the pipeline start failed:
  // the case already exists, so the primary action becomes retrying the start
  // for the SAME case — never re-creating (and never deleting: a lost start
  // response may mean the pipeline is in fact running).
  const [startRetry, setStartRetry] = useState<{
    caseId: string
    runId: string
    cvLibraryId?: string
    overrideDocIds: string[]
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      // The retry state belongs to an already-created case with its documents
      // uploaded — the file list is frozen until the start succeeds.
      if (startRetry) return
      // A submit snapshots `files` into the mutation — anything added while
      // pending would show in the list but never join the in-flight batch,
      // then silently vanish on the reset afterwards.
      if (quickStart.isPending) return
      const newFiles = Array.from(incoming)
      // Never silently drop: name+size+lastModified is a duplicate
      // HEURISTIC, not content identity — genuinely different files can
      // share all three. Everything is kept and a metadata match only
      // warns; byte-identical duplicates are merged server-side by the
      // upload's content-hash dedup, which both rows then resolve to.
      // Computed against `files` from the render closure, NOT inside the
      // setFiles updater: updaters must stay pure (StrictMode invokes them
      // twice to prove it), so a setDropWarnings side effect there would
      // fire double warnings.
      const contentKey = (file: File) => `${file.name}::${file.size}::${file.lastModified}`
      const seen = new Set(files.map(contentKey))
      const suspected = newFiles.filter((file) => seen.has(contentKey(file)))
      if (suspected.length > 0) {
        setDropWarnings((w) => [
          ...w,
          ...suspected.map((f) =>
            t("tender.quickStart.possibleDuplicateWarning", { name: f.name })
          ),
        ])
      }
      setFiles((prev) => [...prev, ...newFiles])
    },
    [startRetry, quickStart.isPending, files, t]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)

      // Must capture entries synchronously — browser clears dataTransfer after event
      const entries = captureDropEntries(e.dataTransfer)

      const processDrop = async () => {
        setProcessingCount((c) => c + 1)
        try {
          const { files, warnings } =
            entries.length === 0
              ? await resolveFileList(e.dataTransfer.files)
              : await resolveEntriesToFiles(entries)

          addFiles(files)
          setDropWarnings((prev) => [...prev, ...warnings])
        } catch {
          setDropWarnings((prev) => [...prev, t("tender.quickStart.readFilesError")])
        } finally {
          setProcessingCount((c) => c - 1)
        }
      }

      void processDrop()
    },
    [addFiles, t]
  )

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return
    setProcessingCount((c) => c + 1)
    try {
      const { files, warnings } = await resolveFileList(e.target.files)
      addFiles(files)
      setDropWarnings((prev) => [...prev, ...warnings])
    } finally {
      setProcessingCount((c) => c - 1)
      e.target.value = ""
    }
  }

  const removeFile = (index: number) => {
    if (startRetry) return
    setFiles((prev) => prev.filter((_, i) => i !== index))
    setOverrideFileIndexes((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    )
  }

  const toggleOverrideFile = (index: number, checked: boolean) => {
    setOverrideFileIndexes((prev) => {
      const next = checked ? [...prev, index] : prev.filter((i) => i !== index)
      return [...new Set(next)].sort((a, b) => a - b)
    })
  }

  const autoLibrary = detectCvLibrary(libraries, cvLibraryKeyword)

  const totalSize = files.reduce((sum, f) => sum + f.size, 0)
  const overCount = files.length > MAX_FILE_COUNT
  const overSize = totalSize > MAX_TOTAL_SIZE
  const blockedByCaps = overCount || overSize
  // The hand-over email leads with this date, and the documents often only
  // point to the procurement platform for it, so a case can't start without it.
  const hasSubmissionDeadline = isDateOnly(submissionDeadlineDraft)
  // Optional, but a half-typed year (0002-…) must block rather than fail the create.
  const questionsDeadlineInvalid =
    questionsDeadlineDraft !== "" && !isPlausibleDate(questionsDeadlineDraft)

  const isSubmitting = quickStart.isPending || retryStart.isPending

  let submitButtonLabel: string
  if (retryStart.isPending) {
    submitButtonLabel = t("tender.quickStart.retryingStart")
  } else if (quickStart.isPending) {
    submitButtonLabel =
      uploadPercent !== null && uploadPercent < 100
        ? t("tender.quickStart.uploading", { percent: uploadPercent })
        : t("tender.quickStart.creating")
  } else if (startRetry) {
    submitButtonLabel = t("tender.quickStart.retryStart")
  } else {
    submitButtonLabel = t("tender.quickStart.create")
  }

  const handleSubmit = () => {
    if (isSubmitting) return
    if (startRetry) {
      // The case and its documents already exist — only the start is retried.
      retryStart.mutate(startRetry, {
        onSuccess: () => {
          setStartRetry(null)
          handleClose()
        },
      })
      return
    }
    if (files.length === 0 || blockedByCaps || !hasSubmissionDeadline || questionsDeadlineInvalid)
      return
    setUploadPercent(0)
    quickStart.mutate(
      {
        title: title.trim() || undefined,
        cvLibraryId: autoLibrary?.id,
        procuringAuthority: procuringAuthority.trim() || undefined,
        sourceDocumentsUrl: sourceDocumentsUrl.trim() || undefined,
        submissionDeadline: submissionDeadlineDraft,
        questionsDeadline: questionsDeadlineDraft || undefined,
        responsible: responsible[0]
          ? {
              oid: responsible[0].oid,
              displayName: responsible[0].displayName,
              email: responsible[0].email,
            }
          : undefined,
        participants: participants.map((p) => ({
          oid: p.oid,
          displayName: p.displayName,
          role: "editor" as const,
        })),
        files,
        overrideFileIndexes,
        onUploadProgress: ({ loaded, total }) => {
          if (total > 0) setUploadPercent(Math.round((loaded / total) * 100))
        },
      },
      {
        onSuccess: () => {
          setUploadPercent(null)
          handleClose()
        },
        onError: (err) => {
          setUploadPercent(null)
          if (err instanceof QuickStartStartError) {
            setStartRetry({
              caseId: err.caseId,
              runId: err.runId,
              cvLibraryId: err.cvLibraryId,
              overrideDocIds: err.overrideDocIds,
            })
          }
        },
      }
    )
  }

  const handleClose = () => {
    if (isSubmitting) return
    setTitle("")
    setProcuringAuthority("")
    setSourceDocumentsUrl("")
    setSubmissionDeadlineDraft("")
    setQuestionsDeadlineDraft("")
    setResponsible([])
    setParticipants([])
    setFiles([])
    setOverrideFileIndexes([])
    setDropWarnings([])
    setProcessingCount(0)
    setStartRetry(null)
    onClose()
  }

  const quickStartErrorMessage = (error: unknown): string => {
    if (error instanceof TenderFileUploadError) {
      const key =
        error.status === 0
          ? "tender.quickStart.uploadNetworkErrorFile"
          : "tender.quickStart.uploadFailedFile"
      return t(key, { name: error.fileName, status: error.status })
    }
    return error instanceof Error ? error.message : t("tender.quickStart.genericError")
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("tender.quickStart.title")}</DialogTitle>
          <DialogDescription>{t("tender.quickStart.description")}</DialogDescription>
        </DialogHeader>

        <DialogBody className="min-h-0 space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="qs-title">{t("tender.quickStart.nameOptional")}</Label>
            <Input
              className="mx-px w-[calc(100%-2px)]"
              disabled={quickStart.isPending}
              id="qs-title"
              placeholder={t("tender.quickStart.namePlaceholder")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="qs-procuring">{t("tender.quickStart.procuringAuthorityLabel")}</Label>
            <Input
              className="mx-px w-[calc(100%-2px)]"
              disabled={quickStart.isPending}
              id="qs-procuring"
              placeholder={t("tender.quickStart.procuringAuthorityPlaceholder")}
              value={procuringAuthority}
              onChange={(e) => setProcuringAuthority(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              {t("tender.quickStart.procuringAuthorityHint")}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="qs-source-url">{t("tender.quickStart.sourceDocumentsUrlLabel")}</Label>
            <Input
              className="mx-px w-[calc(100%-2px)]"
              disabled={quickStart.isPending}
              id="qs-source-url"
              placeholder={t("tender.quickStart.sourceDocumentsUrlPlaceholder")}
              value={sourceDocumentsUrl}
              onChange={(e) => setSourceDocumentsUrl(e.target.value)}
            />
          </div>

          <QuickStartDeadlineFields
            disabled={quickStart.isPending}
            questionsDeadline={questionsDeadlineDraft}
            showQuestionsInvalid={questionsDeadlineInvalid}
            showSubmissionRequired={!startRetry && files.length > 0 && !hasSubmissionDeadline}
            submissionDeadline={submissionDeadlineDraft}
            onQuestionsDeadlineChange={setQuestionsDeadlineDraft}
            onSubmissionDeadlineChange={setSubmissionDeadlineDraft}
          />

          <QuickStartPeopleFields
            disabled={quickStart.isPending}
            participants={participants}
            responsible={responsible}
            onParticipantsChange={setParticipants}
            onResponsibleChange={setResponsible}
          />

          {/* Drop zone */}
          <div className="space-y-1.5">
            <Label>{t("tender.quickStart.documentsLabel")}</Label>
            <div
              role="button"
              tabIndex={0}
              className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/30"
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
              onDragLeave={(e) => {
                e.preventDefault()
                setIsDragging(false)
              }}
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragging(true)
              }}
            >
              {isProcessingDrop ? (
                <div className="flex items-center justify-center gap-2 py-2">
                  <Loader2 className="text-muted-foreground size-5 animate-spin" />
                  <p className="text-muted-foreground text-sm">
                    {t("tender.quickStart.processingFiles")}
                  </p>
                </div>
              ) : (
                <>
                  <Upload className="text-muted-foreground mx-auto mb-2 size-5" />
                  <p className="text-muted-foreground text-sm">
                    {t("tender.quickStart.dropHintBefore")}
                    <span className="text-primary font-medium">
                      {t("tender.quickStart.dropHintClick")}
                    </span>
                  </p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              multiple
              accept={TENDER_ACCEPTED_EXTENSIONS_WITH_ZIP}
              className="hidden"
              type="file"
              onChange={handleInputChange}
            />
            <input
              ref={folderInputRef}
              className="hidden"
              type="file"
              {...({
                webkitdirectory: "",
                directory: "",
              } as React.InputHTMLAttributes<HTMLInputElement>)}
              onChange={handleInputChange}
            />
            {dropWarnings.length > 0 && (
              <div className="border-warning bg-warning/10 text-warning-foreground dark:border-warning dark:bg-warning/30 dark:text-warning-foreground relative rounded-md border px-3 py-2 text-sm">
                <Button
                  aria-label={t("tender.quickStart.closeWarningAria")}
                  className="text-warning hover:text-warning-foreground dark:text-warning-foreground dark:hover:text-warning-foreground absolute top-2 right-2 size-7 p-0"
                  size="icon"
                  type="button"
                  variant="ghost"
                  onClick={() => setDropWarnings([])}
                >
                  <X className="size-3.5" />
                </Button>
                <div className="flex items-start gap-2 pr-6">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                  <ul className="space-y-0.5">
                    {dropWarnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {files.length > 0 && (
              <div className="text-muted-foreground flex items-center justify-between text-xs">
                <span>
                  {t("tender.quickStart.batchSummary", {
                    count: files.length,
                    size: formatFileSize(totalSize),
                  })}
                </span>
                {blockedByCaps && (
                  <span className="text-destructive dark:text-destructive font-medium">
                    {overCount
                      ? t("tender.quickStart.fileCountLimit", { max: MAX_FILE_COUNT })
                      : t("tender.quickStart.totalSizeLimit", {
                          max: formatFileSize(MAX_TOTAL_SIZE),
                        })}
                  </span>
                )}
              </div>
            )}
            <FileList
              files={files}
              overrideFileIndexes={overrideFileIndexes}
              onRemove={removeFile}
              onToggleOverride={toggleOverrideFile}
            />
          </div>

          {startRetry ? (
            <div className="space-y-1">
              <p className="text-destructive text-sm">{t("tender.quickStart.startFailedRetry")}</p>
              {retryStart.isError && (
                <p className="text-destructive text-sm">
                  {retryStart.error instanceof Error
                    ? retryStart.error.message
                    : t("tender.quickStart.genericError")}
                </p>
              )}
            </div>
          ) : (
            quickStart.isError && (
              <p className="text-destructive text-sm">{quickStartErrorMessage(quickStart.error)}</p>
            )
          )}
        </DialogBody>

        {autoLibrary && (
          <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <Library className="size-3.5 shrink-0" />
            <span>{t("tender.quickStart.cvLibrary", { name: autoLibrary.display_name })}</span>
          </div>
        )}

        <DialogFooter>
          <Button disabled={isSubmitting} variant="outline" onClick={handleClose}>
            {t("tender.quickStart.cancel")}
          </Button>
          <Button
            disabled={
              isSubmitting ||
              isProcessingDrop ||
              (!startRetry &&
                (files.length === 0 ||
                  blockedByCaps ||
                  !hasSubmissionDeadline ||
                  questionsDeadlineInvalid))
            }
            onClick={handleSubmit}
          >
            {isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
            {submitButtonLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
